import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import type { ChangeEvent } from "react";
import { useMemo, useRef, useState } from "react";

import { adminUiCopy } from "@/copy/adminUiCopy";
import {
  type OverrideModeSelection,
  type TeamPermissionsOverrideReviewRequest,
  type TeamPermissionsRoleReviewRequest,
  type TeamPermissionsStaffUser,
  useTeamPermissionsPageState,
} from "@/pages/AdminV2/useTeamPermissionsPageState";
import {
  VNextButton,
  VNextDialog,
  VNextKeyValueList,
} from "@/vnext/primitives";
import { SettingsWorkspaceChrome } from "@/vnext/routes/settings/SettingsWorkspaceChrome";
import { cx } from "@/vnext/shared/cx";
import {
  getStaffAvatarInitials,
  getStaffAvatarTone,
} from "@/vnext/shared/staffAvatar";
import "@/vnext/routes/settings/team-permissions/teamPermissionsMake.css";

const TEAM_PERMISSIONS_PAGE_SUBTITLE =
  "Choose a team member, review their roles, and make access changes when needed.";

const ROLE_GUIDANCE_BY_KEY: Record<
  string,
  {
    summary: string;
    notFor: string;
  }
> = {
  super_administrator: {
    summary:
      "Full admin access, including the highest-risk settings and staff access controls.",
    notFor: "routine day-to-day work when Platform Administrator is enough",
  },
  platform_administrator: {
    summary:
      "Full day-to-day admin access across support, growth, finance, and settings.",
    notFor: "customer-only work or a team-specific limited access role",
  },
  finance_administrator: {
    summary:
      "Billing, offers, coupons, refunds, overrides, approvals, and finance reporting.",
    notFor: "support spoofing, CRM ownership, or wider admin ownership",
  },
  finance_viewer: {
    summary: "Read-only finance and analytics access.",
    notFor: "refunds, billing changes, approvals, or admin settings",
  },
  sales_manager: {
    summary:
      "Sales management access for CRM, messaging sequences, and sales exports.",
    notFor: "finance administration, support spoofing, or admin settings",
  },
  sales_representative: {
    summary:
      "Day-to-day sales follow-up access for CRM and messaging sequences.",
    notFor: "exports, finance changes, or support operations",
  },
  marketing_manager: {
    summary:
      "Growth tools for CRM, Forms, Audiences, Email & Comms templates, and Broadcasts.",
    notFor:
      "Automation authoring or publishing, finance administration, support spoofing, or wider admin ownership",
  },
  partner_affiliate_manager: {
    summary:
      "Partner and affiliate access with the related CRM and integration views.",
    notFor: "refunds, billing administration, or support spoofing",
  },
  support_agent: {
    summary: "Support queue and CRM visibility for day-to-day customer help.",
    notFor: "spoofing, approvals, or changing staff access",
  },
  support_lead: {
    summary:
      "Support lead access with spoofing and approval actions for escalations.",
    notFor: "finance administration or changing staff access",
  },
  automation_manager: {
    summary: "Automation authoring, publishing, and Audiences management.",
    notFor:
      "Email & Comms template editing, Broadcasts, billing administration, support spoofing, or general CRM ownership",
  },
};

const getRoleGuidance = (args: { roleKey: string; roleName: string }) => {
  return (
    ROLE_GUIDANCE_BY_KEY[args.roleKey] ?? {
      summary: `Use this role when someone needs ${args.roleName.toLowerCase()} access.`,
      notFor: "full admin access outside this role's area",
    }
  );
};

type PendingRbacReview =
  | {
      kind: "role";
      staffUser: TeamPermissionsStaffUser;
      roleKey: string;
      roleName: string;
      isAssigned: boolean;
    }
  | {
      kind: "override";
      staffUser: TeamPermissionsStaffUser;
      permissionKey: string;
      overrideMode: TeamPermissionsOverrideReviewRequest["overrideMode"];
      reason: string;
    };

const getStaffUserLabel = (staffUser: TeamPermissionsStaffUser): string =>
  staffUser.displayName ?? staffUser.email ?? staffUser.firebaseUid;

const getPendingReviewTitle = (pendingReview: PendingRbacReview): string =>
  pendingReview.kind === "role"
    ? pendingReview.isAssigned
      ? "Remove role"
      : "Assign role"
    : "Apply one-off permission change";

const getPendingReviewEffect = (pendingReview: PendingRbacReview): string => {
  if (pendingReview.kind === "role") {
    return pendingReview.isAssigned
      ? `Remove ${pendingReview.roleName} from ${getStaffUserLabel(
          pendingReview.staffUser,
        )}.`
      : `Assign ${pendingReview.roleName} to ${getStaffUserLabel(
          pendingReview.staffUser,
        )}.`;
  }

  return pendingReview.overrideMode === "grant"
    ? `Grant ${pendingReview.permissionKey} to ${getStaffUserLabel(
        pendingReview.staffUser,
      )}.`
    : `Deny ${pendingReview.permissionKey} for ${getStaffUserLabel(
        pendingReview.staffUser,
      )}.`;
};

const getPendingReviewConfirmLabel = (
  pendingReview: PendingRbacReview,
): string => {
  if (pendingReview.kind === "role") {
    return pendingReview.isAssigned ? "Remove role" : "Assign role";
  }

  return pendingReview.overrideMode === "grant"
    ? "Grant permission"
    : "Deny permission";
};

const getPendingReviewItems = (
  pendingReview: PendingRbacReview,
): Array<{ label: string; value: string }> => {
  const staffUserLabel = getStaffUserLabel(pendingReview.staffUser);

  if (pendingReview.kind === "role") {
    return [
      { label: "Staff member", value: staffUserLabel },
      { label: "Role", value: pendingReview.roleName },
      {
        label: "Action",
        value: pendingReview.isAssigned ? "Remove role" : "Assign role",
      },
      {
        label: "Guardrail",
        value:
          "Uses the existing RBAC audit trail and self-lockout protection.",
      },
    ];
  }

  return [
    { label: "Staff member", value: staffUserLabel },
    { label: "Permission", value: pendingReview.permissionKey },
    {
      label: "Action",
      value:
        pendingReview.overrideMode === "grant"
          ? "Grant permission"
          : "Deny permission",
    },
    { label: "Reason", value: pendingReview.reason },
    {
      label: "Guardrail",
      value:
        "Uses the existing reason, RBAC audit trail, and access audit behavior.",
    },
  ];
};

const groupPermissionKeys = (permissionKeys: string[]) => {
  const groups = new Map<string, string[]>();

  permissionKeys.forEach((permissionKey) => {
    const domain = permissionKey.split(".")[0] ?? "other";
    const current = groups.get(domain) ?? [];
    current.push(permissionKey);
    groups.set(domain, current);
  });

  return Array.from(groups.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([domain, keys]) => ({
      domain,
      keys: keys.sort((left, right) => left.localeCompare(right)),
    }));
};

const getPermissionDomainLabel = (domain: string) => {
  switch (domain) {
    case "forms":
      return "Growth & Forms";
    case "kb":
    case "support":
      return "Knowledge & Support";
    case "learning":
      return "Learning";
    default:
      return domain
        .replace(/_/g, " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
  }
};

const TeamMemberAvatar = ({
  className,
  label,
  photoUrl,
}: {
  className: string;
  label: string | null | undefined;
  photoUrl: string | null | undefined;
}) => (
  // Decorative: the initials or photo always sit beside the visible name.
  <span
    aria-hidden="true"
    className={cx(
      className,
      "vnext-staff-avatar",
      `vnext-staff-avatar--${getStaffAvatarTone(label)}`,
      photoUrl && "has-image",
    )}
  >
    {photoUrl ? (
      <img alt="" src={photoUrl} />
    ) : (
      <span className="vnext-staff-avatar__mark">
        {getStaffAvatarInitials(label)}
      </span>
    )}
  </span>
);

export default function TeamPermissionsRoute() {
  const state = useTeamPermissionsPageState();
  const [effectivePermissionSearch, setEffectivePermissionSearch] =
    useState("");
  const [pendingReview, setPendingReview] = useState<PendingRbacReview | null>(
    null,
  );
  const [isConfirmingReview, setIsConfirmingReview] = useState(false);
  const confirmInFlightRef = useRef(false);

  const selectedStaffUserLabel =
    state.selectedStaffUser?.displayName ??
    state.selectedStaffUser?.email ??
    state.selectedStaffUser?.firebaseUid ??
    "Selected staff user";
  const normalizedEffectivePermissionSearch = effectivePermissionSearch
    .trim()
    .toLowerCase();
  const groupedEffectivePermissionKeys = useMemo(
    () =>
      groupPermissionKeys(
        [...(state.meData?.permissionKeys ?? [])].filter((permissionKey) =>
          normalizedEffectivePermissionSearch.length === 0
            ? true
            : permissionKey
                .toLowerCase()
                .includes(normalizedEffectivePermissionSearch),
        ),
      ),
    [normalizedEffectivePermissionSearch, state.meData?.permissionKeys],
  );
  const groupedPermissionOptions = useMemo(
    () =>
      groupPermissionKeys(
        state.permissions.map((permission) => permission.permissionKey),
      ),
    [state.permissions],
  );
  const hasNoStaffSearchResults =
    state.staffSearch.trim().length > 0 &&
    !state.hasPendingStaffSearch &&
    state.staffUsers.length === 0;
  const pendingReviewItems = pendingReview
    ? getPendingReviewItems(pendingReview)
    : [];

  const closePendingReview = (): void => {
    if (isConfirmingReview) {
      return;
    }

    setPendingReview(null);
  };

  const requestRoleReview = (
    role: { roleKey: string; roleName: string },
    isAssigned: boolean,
  ): void => {
    if (!state.selectedStaffUser) {
      return;
    }

    const blockReason = state.getRoleToggleBlockReason(
      role.roleKey,
      isAssigned,
    );
    if (blockReason) {
      return;
    }

    setPendingReview({
      kind: "role",
      staffUser: state.selectedStaffUser,
      roleKey: role.roleKey,
      roleName: role.roleName,
      isAssigned,
    });
  };

  const requestOverrideReview = (): void => {
    if (
      !state.selectedStaffUser ||
      state.overrideUpsertBlockReason !== null ||
      state.overrideMode === ""
    ) {
      return;
    }

    setPendingReview({
      kind: "override",
      staffUser: state.selectedStaffUser,
      permissionKey: state.overridePermissionKey,
      overrideMode: state.overrideMode,
      reason: state.overrideReason.trim(),
    });
  };

  const confirmPendingReview = async (): Promise<void> => {
    if (!pendingReview || confirmInFlightRef.current) {
      return;
    }

    confirmInFlightRef.current = true;
    setIsConfirmingReview(true);

    try {
      if (pendingReview.kind === "role") {
        const request: TeamPermissionsRoleReviewRequest = {
          staffUser: pendingReview.staffUser,
          roleKey: pendingReview.roleKey,
          isAssigned: pendingReview.isAssigned,
        };
        await state.runRoleToggle(request.roleKey, request.isAssigned, request);
      } else {
        const request: TeamPermissionsOverrideReviewRequest = {
          staffUser: pendingReview.staffUser,
          permissionKey: pendingReview.permissionKey,
          overrideMode: pendingReview.overrideMode,
          reason: pendingReview.reason,
        };
        await state.runOverrideUpsert(request);
      }

      setPendingReview(null);
    } finally {
      confirmInFlightRef.current = false;
      setIsConfirmingReview(false);
    }
  };

  if (
    (state.staffUsersLoading && !state.staffUsersData) ||
    (state.rolesLoading && !state.rolesData) ||
    (state.permissionsLoading && !state.permissionsData)
  ) {
    return (
      <SettingsWorkspaceChrome
        subtitle={TEAM_PERMISSIONS_PAGE_SUBTITLE}
        title="Team and permissions"
      >
        <section className="team-make-page">
          <div className="team-make-panel">
            <div className="team-make-empty">
              Loading staff users, roles, and permissions...
            </div>
          </div>
        </section>
      </SettingsWorkspaceChrome>
    );
  }

  if (state.staffUsersError || state.rolesError || state.permissionsError) {
    return (
      <SettingsWorkspaceChrome
        subtitle={TEAM_PERMISSIONS_PAGE_SUBTITLE}
        title="Team and permissions"
      >
        <section className="team-make-page">
          <div className="team-make-panel">
            <div className="team-make-warning">
              We couldn&apos;t load staff users, roles, or permissions. Check
              the admin services, then try again.
            </div>
          </div>
        </section>
      </SettingsWorkspaceChrome>
    );
  }

  return (
    <SettingsWorkspaceChrome
      subtitle={TEAM_PERMISSIONS_PAGE_SUBTITLE}
      title="Team and permissions"
    >
      <section className="team-make-page">
        {state.actionError ? (
          <div className="team-make-warning" role="alert">
            {state.actionError}
          </div>
        ) : null}

        <div className="team-make-layout">
          <section className="team-make-panel team-make-staff-panel">
            <div className="team-make-panel__header">
              <div className="team-make-panel__header-copy">
                <h2>Team members</h2>
                <span>{state.staffUsers.length}</span>
              </div>
              <button
                aria-label="Refresh staff list"
                className="team-make-icon-button"
                onClick={state.refreshStaffUsers}
                title={adminUiCopy.hoverHelp.teamPermissions.refreshStaffList}
                type="button"
              >
                <RefreshOutlinedIcon sx={{ fontSize: 13 }} />
              </button>
            </div>

            <div className="team-make-staff-search">
              <SearchIcon sx={{ fontSize: 13 }} />
              <input
                aria-label="Search staff users"
                onChange={(event) => state.setSearchValue(event.target.value)}
                placeholder="Search staff users"
                title={adminUiCopy.hoverHelp.teamPermissions.searchStaffUsers}
                type="text"
                value={state.staffSearch}
              />
            </div>
            <div className="team-make-inline-warning">
              Search by name, email, or Firebase UID. Results update after you
              stop typing.
            </div>
            {state.hasPendingStaffSearch ? (
              <div className="team-make-inline-warning" role="status">
                Waiting for you to stop typing before updating staff users.
              </div>
            ) : null}

            <div
              aria-label="Staff users"
              className="team-make-staff-list"
              role="listbox"
            >
              {hasNoStaffSearchResults ? (
                <div className="team-make-empty" role="status">
                  No staff users match this search. Clear the search or try a
                  name, email, or Firebase UID.
                </div>
              ) : null}
              {state.staffUsers.map((user) => {
                const displayLabel =
                  user.displayName ?? user.email ?? user.firebaseUid;
                const isSelected = state.selectedStaffUserId === user.id;
                const isCurrentUser = state.meData?.staffUser.id === user.id;

                // WCAG 2.5.3 Label in Name: the visible name, role count and
                // "You" name the row, in order; a hidden suffix adds email
                // and status. The spaces keep those parts separate words;
                // flex layout does not render them.
                return (
                  <button
                    aria-selected={isSelected}
                    className={`team-make-staff-row${
                      isSelected ? " is-selected" : ""
                    }`}
                    key={user.id}
                    onClick={() => state.trackStaffSelection(user.id)}
                    role="option"
                    type="button"
                  >
                    <TeamMemberAvatar
                      className="team-make-staff-row__avatar"
                      label={displayLabel}
                      photoUrl={user.profilePhotoUrl}
                    />
                    <span className="team-make-staff-row__copy">
                      <span className="team-make-staff-row__name">
                        {displayLabel}
                      </span>{" "}
                      <span className="team-make-staff-row__meta">
                        {`${user.roles.length} role${
                          user.roles.length === 1 ? "" : "s"
                        }`}
                      </span>
                    </span>{" "}
                    {isCurrentUser ? (
                      <>
                        <span className="team-make-pill team-make-pill--you">
                          You
                        </span>{" "}
                      </>
                    ) : null}
                    <span className="vnext-sr-only">
                      {`Email ${user.email ?? "not available"}, ${
                        user.isActive ? "active" : "inactive"
                      }`}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <div className="team-make-detail">
            <section
              aria-label={`Details for ${selectedStaffUserLabel}`}
              className="team-make-panel"
            >
              <div className="team-make-user-banner">
                <TeamMemberAvatar
                  className="team-make-user-banner__avatar"
                  label={selectedStaffUserLabel}
                  photoUrl={state.selectedStaffUser?.profilePhotoUrl}
                />
                <div className="team-make-user-banner__copy">
                  <div className="team-make-user-banner__title-row">
                    <h2>{selectedStaffUserLabel}</h2>
                    {state.meData?.staffUser.id ===
                    state.selectedStaffUser?.id ? (
                      <span className="team-make-pill team-make-pill--you">
                        You
                      </span>
                    ) : null}
                  </div>
                  <p>
                    {state.selectedStaffUser?.email ??
                      state.selectedStaffUser?.firebaseUid ??
                      "Select a staff user to inspect access"}
                  </p>
                </div>
                <div className="team-make-user-banner__roles">
                  {(state.selectedStaffUser?.roles ?? []).map((role) => (
                    <span
                      className="team-make-pill team-make-pill--role"
                      key={role.roleKey}
                    >
                      {role.roleName}
                    </span>
                  ))}
                </div>
              </div>
            </section>

            <section aria-label="Role assignments" className="team-make-panel">
              <div className="team-make-panel__header">
                <div className="team-make-panel__header-copy">
                  <h2>Roles for this person</h2>
                  <span>
                    {`${state.selectedStaffUser?.roles.length ?? 0} of ${
                      state.roles.length
                    } assigned`}
                  </span>
                </div>
              </div>

              <div className="team-make-role-groups">
                <div className="team-make-info">
                  <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                  <p>
                    Use roles for normal job-based access. Use one-off
                    permission changes only when a role is close but not quite
                    right.
                  </p>
                </div>
                {groupedPermissionOptions.length === 0 &&
                state.roles.length === 0 ? (
                  <div className="team-make-empty">
                    No role definitions are available.
                  </div>
                ) : null}
                {[
                  {
                    domain: "Platform",
                    keys: ["super_administrator", "platform_administrator"],
                  },
                  {
                    domain: "Finance",
                    keys: ["finance_administrator", "finance_viewer"],
                  },
                  {
                    domain: "Sales & Growth",
                    keys: [
                      "sales_manager",
                      "sales_representative",
                      "marketing_manager",
                      "partner_affiliate_manager",
                    ],
                  },
                  {
                    domain: "Support & Ops",
                    keys: [
                      "support_agent",
                      "support_lead",
                      "automation_manager",
                    ],
                  },
                  {
                    domain: "Other",
                    keys: state.roles.map((role) => role.roleKey),
                  },
                ]
                  .map((group) => ({
                    domain: group.domain,
                    roles: state.roles.filter((role) =>
                      group.keys.includes(role.roleKey),
                    ),
                  }))
                  .filter(
                    (group, index, groups) =>
                      group.roles.length > 0 &&
                      (group.domain !== "Other" ||
                        groups.every(
                          (candidate) =>
                            candidate.domain === "Other" ||
                            candidate.roles.length === 0,
                        ) ||
                        group.roles.some(
                          (role) =>
                            !groups
                              .filter(
                                (candidate) => candidate.domain !== "Other",
                              )
                              .some((candidate) =>
                                candidate.roles.some(
                                  (groupedRole) =>
                                    groupedRole.roleKey === role.roleKey,
                                ),
                              ),
                        )) &&
                      groups.findIndex(
                        (candidate) => candidate.domain === group.domain,
                      ) === index,
                  )
                  .map((group) => (
                    <div className="team-make-role-group" key={group.domain}>
                      <div className="team-make-role-group__label">
                        {group.domain}
                      </div>
                      <div className="team-make-role-group__rows">
                        {group.roles.map((role) => {
                          const isAssigned = state.assignedRoleKeys.has(
                            role.roleKey,
                          );
                          const roleGuidance = getRoleGuidance({
                            roleKey: role.roleKey,
                            roleName: role.roleName,
                          });
                          const blockReason = state.getRoleToggleBlockReason(
                            role.roleKey,
                            isAssigned,
                          );

                          return (
                            <div
                              className="team-make-role-row"
                              key={role.roleKey}
                            >
                              <div className="team-make-role-row__copy">
                                <div className="team-make-role-row__name">
                                  {role.roleName}
                                </div>
                                <div className="team-make-role-row__summary">
                                  {roleGuidance.summary}
                                </div>
                                <div className="team-make-role-row__key">
                                  {role.roleKey}
                                </div>
                                <div className="team-make-role-row__not-for">
                                  {`Not for: ${roleGuidance.notFor}.`}
                                </div>
                                {blockReason ? (
                                  <div className="team-make-inline-warning">
                                    {blockReason}
                                  </div>
                                ) : null}
                              </div>
                              <button
                                aria-label={`${
                                  isAssigned ? "Remove role" : "Assign role"
                                } for ${role.roleName} role for ${selectedStaffUserLabel}`}
                                className={`team-make-role-row__action${
                                  isAssigned ? " is-remove" : ""
                                }`}
                                disabled={
                                  state.isAssignStaffRoleLoading ||
                                  !state.selectedStaffUser ||
                                  Boolean(blockReason)
                                }
                                onClick={() =>
                                  requestRoleReview(role, isAssigned)
                                }
                                title={
                                  isAssigned
                                    ? "Review before removing this role from the selected staff user."
                                    : "Review before assigning this role to the selected staff user."
                                }
                                type="button"
                              >
                                {isAssigned ? "Remove" : "Assign"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            </section>

            <section
              aria-label="One-off permission changes"
              className="team-make-panel"
            >
              <div className="team-make-panel__header">
                <div className="team-make-panel__header-copy">
                  <h2>One-off permission changes</h2>
                  <span className="team-make-pill team-make-pill--count">
                    {state.selectedStaffUser?.permissionOverrides.length ?? 0}
                  </span>
                </div>
                <SecurityOutlinedIcon sx={{ fontSize: 14 }} />
              </div>

              <div className="team-make-info">
                <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                <p>
                  A one-off change affects one permission only. It does not
                  change anything else in that role or area.
                </p>
              </div>
              <div className="team-make-info">
                <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                <p>
                  Choose one permission, decide whether to grant or deny it,
                  then add a short reason.
                </p>
              </div>

              {(state.selectedStaffUser?.permissionOverrides ?? []).length ===
              0 ? (
                <div className="team-make-empty">
                  No overrides configured for this user
                </div>
              ) : (
                <div className="team-make-chip-row">
                  {state.selectedStaffUser?.permissionOverrides.map(
                    (override) => (
                      <span
                        className={`team-make-chip${
                          override.isGranted ? " is-grant" : " is-deny"
                        }`}
                        key={override.id}
                      >
                        {`${override.permissionKey} · ${
                          override.isGranted ? "Grant" : "Deny"
                        }`}
                      </span>
                    ),
                  )}
                </div>
              )}

              <div className="team-make-override-card">
                <div className="team-make-override-card__label">
                  Add one-off change
                </div>
                <div className="team-make-override-card__row">
                  <label
                    className="team-make-sr-only"
                    htmlFor="team-permissions-permission"
                  >
                    Permission
                  </label>
                  <select
                    id="team-permissions-permission"
                    title={adminUiCopy.hoverHelp.teamPermissions.permissionKey}
                    onChange={(event) =>
                      state.setOverridePermission(event.target.value)
                    }
                    value={state.overridePermissionKey}
                  >
                    <option value="">Choose a permission key</option>
                    {groupedPermissionOptions.map((group) => (
                      <optgroup
                        key={group.domain}
                        label={getPermissionDomainLabel(group.domain)}
                      >
                        {group.keys.map((permissionKey) => (
                          <option key={permissionKey} value={permissionKey}>
                            {permissionKey}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <div className="team-make-inline-warning">
                    {adminUiCopy.hoverHelp.teamPermissions.permissionKey}
                  </div>

                  <label
                    className="team-make-sr-only"
                    htmlFor="team-permissions-action"
                  >
                    Action
                  </label>
                  <select
                    id="team-permissions-action"
                    title={adminUiCopy.hoverHelp.teamPermissions.overrideAction}
                    onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                      state.setOverrideModeValue(
                        event.target.value as OverrideModeSelection,
                      )
                    }
                    value={state.overrideMode}
                  >
                    <option value="">Choose change</option>
                    <option value="grant">Grant</option>
                    <option value="deny">Deny</option>
                  </select>
                  <div className="team-make-inline-warning">
                    {adminUiCopy.hoverHelp.teamPermissions.overrideAction}
                  </div>

                  <label
                    className="team-make-sr-only"
                    htmlFor="team-permissions-reason"
                  >
                    Reason
                  </label>
                  <input
                    id="team-permissions-reason"
                    onChange={(event) =>
                      state.setOverrideReasonValue(event.target.value)
                    }
                    placeholder="Reason for audit log (required)"
                    type="text"
                    value={state.overrideReason}
                  />

                  <button
                    disabled={
                      state.isSetPermissionOverrideLoading ||
                      Boolean(state.overrideUpsertBlockReason)
                    }
                    onClick={() => requestOverrideReview()}
                    title="Review this grant or deny override before applying it."
                    type="button"
                  >
                    Apply change
                  </button>
                </div>
                {state.overrideUpsertBlockReason ? (
                  <div className="team-make-inline-warning">
                    {state.overrideUpsertBlockReason}
                  </div>
                ) : null}
              </div>

              <div className="team-make-risk" role="alert">
                <ReportProblemOutlinedIcon sx={{ fontSize: 13 }} />
                <p>
                  One-off changes take effect straight away after review and can
                  open or block sensitive admin actions.
                </p>
              </div>
            </section>

            <section
              aria-label="Effective permissions"
              className="team-make-panel"
            >
              <div className="team-make-panel__header">
                <div className="team-make-panel__header-copy">
                  <h2>Your own access keys</h2>
                  <span>
                    {`${(state.meData?.permissionKeys ?? []).length} total`}
                  </span>
                </div>
              </div>

              <div className="team-make-staff-search team-make-staff-search--compact">
                <SearchIcon sx={{ fontSize: 13 }} />
                <input
                  aria-label="Search your own access keys"
                  onChange={(event) =>
                    setEffectivePermissionSearch(event.target.value)
                  }
                  placeholder="Search access keys…"
                  title={
                    adminUiCopy.hoverHelp.teamPermissions
                      .filterEffectivePermissions
                  }
                  type="text"
                  value={effectivePermissionSearch}
                />
              </div>
              <div className="team-make-info">
                <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                <p>
                  {adminUiCopy.hoverHelp.teamPermissions.inheritedPermissions}
                </p>
              </div>

              {groupedEffectivePermissionKeys.length === 0 ? (
                <div className="team-make-empty">
                  No access keys match this search.
                </div>
              ) : (
                <div className="team-make-permission-groups">
                  {groupedEffectivePermissionKeys.map((group) => (
                    <div
                      className="team-make-permission-group"
                      key={group.domain}
                    >
                      <div className="team-make-permission-group__header">
                        <span>{getPermissionDomainLabel(group.domain)}</span>
                        <span>{`(${group.keys.length})`}</span>
                      </div>
                      <div className="team-make-chip-row">
                        {group.keys.map((permissionKey) => (
                          <span className="team-make-chip" key={permissionKey}>
                            {permissionKey}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
        {pendingReview ? (
          <VNextDialog
            description={getPendingReviewEffect(pendingReview)}
            footer={
              <>
                <VNextButton
                  disabled={isConfirmingReview}
                  onClick={closePendingReview}
                  tone="quiet"
                >
                  Cancel
                </VNextButton>
                <VNextButton
                  disabled={isConfirmingReview}
                  onClick={() => {
                    void confirmPendingReview();
                  }}
                  tone="primary"
                >
                  {isConfirmingReview
                    ? "Applying..."
                    : getPendingReviewConfirmLabel(pendingReview)}
                </VNextButton>
              </>
            }
            onClose={closePendingReview}
            open
            title={getPendingReviewTitle(pendingReview)}
          >
            <VNextKeyValueList items={pendingReviewItems} />
          </VNextDialog>
        ) : null}
      </section>
    </SettingsWorkspaceChrome>
  );
}
